import React, { useState, useEffect, useContext } from "react";
import Papa from "papaparse";
import {
  collection,
  query,
  orderBy,
  limit,
  limitToLast,
  startAfter,
  endBefore,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaBug,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUpload,
  FaList,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaBuilding,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";

// Admin UIDs who can delete entries
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];

// State to Country mapping
const STATE_COUNTRY_MAP = {
  // US States
  AL: "United States",
  AK: "United States",
  AZ: "United States",
  AR: "United States",
  CA: "United States",
  CO: "United States",
  CT: "United States",
  DC: "United States",
  DE: "United States",
  FL: "United States",
  GA: "United States",
  HI: "United States",
  IA: "United States",
  ID: "United States",
  IL: "United States",
  IN: "United States",
  KS: "United States",
  KY: "United States",
  LA: "United States",
  MA: "United States",
  MD: "United States",
  ME: "United States",
  MI: "United States",
  MN: "United States",
  MO: "United States",
  MS: "United States",
  MT: "United States",
  NC: "United States",
  ND: "United States",
  NE: "United States",
  NH: "United States",
  NJ: "United States",
  NM: "United States",
  NV: "United States",
  NY: "United States",
  OH: "United States",
  OK: "United States",
  OR: "United States",
  PA: "United States",
  RI: "United States",
  SC: "United States",
  SD: "United States",
  TN: "United States",
  TX: "United States",
  UT: "United States",
  VA: "United States",
  VT: "United States",
  WA: "United States",
  WI: "United States",
  WV: "United States",
  WY: "United States",
  // US Territories
  PR: "United States",
  VI: "United States",
  // Canada
  MB: "Canada",
  ON: "Canada",
  QC: "Canada",
  SK: "Canada",
  AB: "Canada",
  BC: "Canada",
  NS: "Canada",
  NB: "Canada",
  QU: "Canada",
  // Other
  CH: "Switzerland",
};

// Get country from state code
const getCountryFromState = (state) => {
  if (!state) return "";
  const stateCode = state.toUpperCase().trim();
  return STATE_COUNTRY_MAP[stateCode] || "";
};

// Fuzzy matching utility using Levenshtein distance
const levenshteinDistance = (str1, str2) => {
  const matrix = [];
  const len1 = str1.length;
  const len2 = str2.length;

  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[len2][len1];
};

// Normalize company name for better matching
const normalizeCompanyName = (name) => {
  if (!name) return "";

  return (
    name
      .toLowerCase()
      .trim()
      // Remove common prefixes/suffixes
      .replace(/^(1-800-|1800-|the\s+)/g, "")
      .replace(/\s+(llc|inc|corp|corporation|company|co|ltd|limited)\.?$/g, "")
      // Remove punctuation and normalize spaces
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
};

// Enhanced company name similarity with partial matching
const calculateCompanySimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;

  const original1 = str1.toLowerCase().trim();
  const original2 = str2.toLowerCase().trim();

  // Exact match
  if (original1 === original2) return 100;

  // Normalize both names
  const normalized1 = normalizeCompanyName(str1);
  const normalized2 = normalizeCompanyName(str2);

  // Exact match after normalization
  if (normalized1 === normalized2 && normalized1.length > 0) return 95;

  // Check if one is contained in the other (partial match)
  if (normalized1.length > 3 && normalized2.length > 3) {
    if (
      normalized1.includes(normalized2) ||
      normalized2.includes(normalized1)
    ) {
      const shorterLen = Math.min(normalized1.length, normalized2.length);
      const longerLen = Math.max(normalized1.length, normalized2.length);
      // Higher score for better length ratio
      const lengthRatio = shorterLen / longerLen;
      return Math.round(85 * lengthRatio);
    }
  }

  // Word-based matching for multi-word companies
  const words1 = normalized1.split(/\s+/).filter((w) => w.length > 2);
  const words2 = normalized2.split(/\s+/).filter((w) => w.length > 2);

  if (words1.length > 0 && words2.length > 0) {
    let matchingWords = 0;
    for (const word1 of words1) {
      for (const word2 of words2) {
        if (word1 === word2 || word1.includes(word2) || word2.includes(word1)) {
          matchingWords++;
          break;
        }
      }
    }
    const wordMatchRatio =
      matchingWords / Math.max(words1.length, words2.length);
    if (wordMatchRatio > 0.5) {
      return Math.round(75 * wordMatchRatio);
    }
  }

  // Fall back to Levenshtein distance
  const maxLen = Math.max(normalized1.length, normalized2.length);
  if (maxLen === 0) return 0;

  const distance = levenshteinDistance(normalized1, normalized2);
  return Math.round(((maxLen - distance) / maxLen) * 100);
};

// Calculate similarity percentage (0-100) for general text
const calculateSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  const normalized1 = str1.toLowerCase().trim();
  const normalized2 = str2.toLowerCase().trim();

  if (normalized1 === normalized2) return 100;

  const maxLen = Math.max(normalized1.length, normalized2.length);
  const distance = levenshteinDistance(normalized1, normalized2);
  return Math.round(((maxLen - distance) / maxLen) * 100);
};

// Normalize address for comparison
const normalizeAddress = (address) => {
  if (!address) return "";
  return (
    address
      .toLowerCase()
      .trim()
      // Normalize common address abbreviations
      .replace(/\b(street|st\.?)\b/g, "st")
      .replace(/\b(avenue|ave\.?)\b/g, "ave")
      .replace(/\b(road|rd\.?)\b/g, "rd")
      .replace(/\b(drive|dr\.?)\b/g, "dr")
      .replace(/\b(boulevard|blvd\.?)\b/g, "blvd")
      .replace(/\b(lane|ln\.?)\b/g, "ln")
      .replace(/\b(court|ct\.?)\b/g, "ct")
      .replace(/\b(place|pl\.?)\b/g, "pl")
      .replace(/\b(suite|ste\.?)\b/g, "ste")
      .replace(/\b(apartment|apt\.?)\b/g, "apt")
      .replace(/\b(building|bldg\.?)\b/g, "bldg")
      // Normalize directionals
      .replace(/\b(north|n\.?)\b/g, "n")
      .replace(/\b(south|s\.?)\b/g, "s")
      .replace(/\b(east|e\.?)\b/g, "e")
      .replace(/\b(west|w\.?)\b/g, "w")
      .replace(/\b(northeast|ne\.?)\b/g, "ne")
      .replace(/\b(northwest|nw\.?)\b/g, "nw")
      .replace(/\b(southeast|se\.?)\b/g, "se")
      .replace(/\b(southwest|sw\.?)\b/g, "sw")
      // Remove extra punctuation and normalize spaces
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
};

// Enhanced address similarity with fuzzy matching
const calculateAddressSimilarity = (addr1, addr2) => {
  if (!addr1 || !addr2) return 0;

  const original1 = addr1.toLowerCase().trim();
  const original2 = addr2.toLowerCase().trim();

  // Exact match
  if (original1 === original2) return 100;

  // Normalize both addresses
  const normalized1 = normalizeAddress(addr1);
  const normalized2 = normalizeAddress(addr2);

  // Exact match after normalization
  if (normalized1 === normalized2 && normalized1.length > 0) return 95;

  // Extract street numbers for comparison
  const getStreetNumber = (addr) => {
    const match = addr.match(/^\s*(\d+)/);
    return match ? match[1] : "";
  };

  const num1 = getStreetNumber(normalized1);
  const num2 = getStreetNumber(normalized2);

  // If street numbers are different, lower the max possible score
  const streetNumberMatch = num1 && num2 ? (num1 === num2 ? 1.0 : 0.7) : 0.9;

  // Check for partial containment
  if (normalized1.length > 5 && normalized2.length > 5) {
    if (
      normalized1.includes(normalized2) ||
      normalized2.includes(normalized1)
    ) {
      const shorterLen = Math.min(normalized1.length, normalized2.length);
      const longerLen = Math.max(normalized1.length, normalized2.length);
      const lengthRatio = shorterLen / longerLen;
      return Math.round(80 * lengthRatio * streetNumberMatch);
    }
  }

  // Word-based matching
  const words1 = normalized1.split(/\s+/).filter((w) => w.length > 1);
  const words2 = normalized2.split(/\s+/).filter((w) => w.length > 1);

  if (words1.length > 0 && words2.length > 0) {
    let matchingWords = 0;
    for (const word1 of words1) {
      for (const word2 of words2) {
        if (word1 === word2) {
          matchingWords++;
          break;
        }
      }
    }
    const wordMatchRatio =
      matchingWords / Math.max(words1.length, words2.length);
    if (wordMatchRatio > 0.4) {
      return Math.round(70 * wordMatchRatio * streetNumberMatch);
    }
  }

  // Fall back to Levenshtein distance
  const maxLen = Math.max(normalized1.length, normalized2.length);
  if (maxLen === 0) return 0;

  const distance = levenshteinDistance(normalized1, normalized2);
  return Math.round(((maxLen - distance) / maxLen) * 100 * streetNumberMatch);
};

// Enhanced validation for generator data
const validateGenerator = (data) => {
  const errors = [];

  if (!data.name || typeof data.name !== "string" || !data.name.trim()) {
    errors.push("Company name is required");
  }

  if (
    !data.address ||
    typeof data.address !== "string" ||
    !data.address.trim()
  ) {
    errors.push("Address is required");
  }

  if (data.name && data.name.length > 200) {
    errors.push("Company name is too long (max 200 characters)");
  }

  if (data.address && data.address.length > 500) {
    errors.push("Address is too long (max 500 characters)");
  }

  return {
    valid: errors.length === 0,
    errors,
    data:
      errors.length === 0
        ? {
            name: data.name.trim(),
            address: data.address.trim(),
            city: data.city?.trim() || "",
            state: data.state?.trim() || "",
            country: data.country?.trim() || "",
            type: data.type?.trim() || "",
            notes: data.notes?.trim() || "",
          }
        : null,
  };
};

const GeneratorChecker = ({
  teamId,
  selectedTeam,
  teams,
  onTeamChange,
  debugEnabled = false,
  onDebugToggle,
}) => {
  const { user } = useContext(AuthContext);

  // User permissions
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);
  const canAdd = !!user; // Any authenticated user can add
  const canDelete = isAdmin; // Only admins can delete
  const canEdit = !!user; // Any authenticated user can edit

  // State for the checker functionality
  const [checkName, setCheckName] = useState("");
  const [checkAddress, setCheckAddress] = useState("");
  const [checkResults, setCheckResults] = useState([]);
  const [checking, setChecking] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("checker"); // "checker", "manage", "import"

  // Manage generators state
  const PAGE_SIZE = 10;
  const [manageLoading, setManageLoading] = useState(false);
  const [listGenerators, setListGenerators] = useState([]);
  const [firstKey, setFirstKey] = useState(null);
  const [lastKey, setLastKey] = useState(null);
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  // Add generator state
  const [addData, setAddData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    country: "",
    type: "",
    notes: "",
  });
  const [addErrors, setAddErrors] = useState([]);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [editingErrors, setEditingErrors] = useState([]);

  // CSV import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvHasHeader, setCsvHasHeader] = useState(true);
  const [testMode, setTestMode] = useState(false); // New: test mode toggle
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    skipped: 0,
    errors: 0,
  });

  // Debug logging
  const debugLog = (...args) => {
    if (debugEnabled) {
      console.log("[GeneratorChecker]", ...args);
    }
  };

  // Load generators_existing collection reference
  const collRef = teamId
    ? collection(db, "teams", teamId, "generators_existing")
    : null;

  // Check generators against existing list
  const checkGenerators = async () => {
    if (!user) {
      return toast.error("You must be logged in to use this feature");
    }

    if (!teamId) {
      return toast.error("No team selected");
    }

    if (!checkName.trim() && !checkAddress.trim()) {
      return toast.error("Enter at least a company name or address");
    }

    setChecking(true);
    debugLog("=== GENERATOR CHECK START ===");
    debugLog("Team ID:", teamId);
    debugLog("Check Name:", checkName);
    debugLog("Check Address:", checkAddress);

    try {
      // Get all generators from the team
      const snap = await getDocs(query(collRef, orderBy("name")));
      const generators = [];
      snap.forEach((doc) => {
        const data = doc.data();
        generators.push({
          id: doc.id,
          name: data.customerName || data.name || "",
          address: data.location
            ? `${data.location.street || ""} ${data.location.city || ""} ${
                data.location.state || ""
              } ${data.location.zipCode || ""}`.trim()
            : data.address || "",
          ...data,
        });
      });

      const results = [];
      const inputName = checkName.trim();
      const inputAddress = checkAddress.trim();

      for (const generator of generators) {
        let nameMatch = 0;
        let addressMatch = 0;
        let overallMatch = false;

        // Check name similarity if provided (using enhanced company matching)
        if (inputName) {
          nameMatch = calculateCompanySimilarity(inputName, generator.name);
        }

        // Check address similarity if provided (using enhanced address matching)
        if (inputAddress) {
          addressMatch = calculateAddressSimilarity(
            inputAddress,
            generator.address
          );
        }

        // Determine if this is a match (adjusted thresholds for better partial matching)
        if (nameMatch >= 75 || addressMatch >= 80) {
          overallMatch = true;
        }

        // Include in results if there's any reasonable similarity (lowered thresholds)
        if (nameMatch >= 50 || addressMatch >= 60) {
          results.push({
            generator,
            nameMatch,
            addressMatch,
            overallMatch,
            confidence: Math.max(nameMatch, addressMatch),
          });
        }
      }

      // Sort by confidence (highest first)
      results.sort((a, b) => b.confidence - a.confidence);

      setCheckResults(results);
      debugLog("Check complete:", results);

      if (results.length === 0) {
        toast.success("✓ No similar generators found - appears to be new");
      } else {
        const matches = results.filter((r) => r.overallMatch);
        if (matches.length > 0) {
          toast.warning(
            `⚠️ Found ${matches.length} potential match(es) - review carefully`
          );
        } else {
          toast.info(
            `Found ${results.length} similar generator(s) with lower confidence`
          );
        }
      }
    } catch (err) {
      console.error("Generator check error:", err);
      toast.error("Check failed: " + err.message);
    } finally {
      setChecking(false);
    }
  };

  // Load page of generators
  const loadPage = async (opts = {}) => {
    if (!user || !teamId) return;
    setManageLoading(true);
    try {
      let q = null;
      if (opts.direction === "next" && lastKey) {
        q = query(
          collRef,
          orderBy("name"),
          startAfter(lastKey),
          limit(PAGE_SIZE)
        );
      } else if (opts.direction === "prev" && firstKey) {
        q = query(
          collRef,
          orderBy("name"),
          endBefore(firstKey),
          limitToLast(PAGE_SIZE)
        );
      } else {
        q = query(collRef, orderBy("name"), limit(PAGE_SIZE));
      }

      const snap = await getDocs(q);
      const items = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.customerName || data.name || "",
          address: data.location
            ? `${data.location.street || ""}, ${data.location.city || ""}, ${
                data.location.state || ""
              } ${data.location.zipCode || ""}`
                .replace(/,\s*,/g, ",")
                .trim()
            : data.address || "",
          ...data,
        };
      });

      setListGenerators(items);
      setFirstKey(items.length ? items[0].name : null);
      setLastKey(items.length ? items[items.length - 1].name : null);

      // Check for next page
      if (items.length === PAGE_SIZE) {
        const peekQ = query(
          collRef,
          orderBy("name"),
          startAfter(items[items.length - 1].name),
          limit(1)
        );
        const peek = await getDocs(peekQ);
        setHasNext(!peek.empty);
      } else {
        setHasNext(false);
      }

      // Check for previous page
      if (items.length) {
        const peekPrevQ = query(
          collRef,
          orderBy("name"),
          endBefore(items[0].name),
          limitToLast(1)
        );
        const peekPrev = await getDocs(peekPrevQ);
        setHasPrev(!peekPrev.empty);
      } else {
        setHasPrev(false);
      }
    } catch (err) {
      toast.error(`Failed to load generators: ${err.message}`);
    } finally {
      setManageLoading(false);
    }
  };

  // Load initial page
  useEffect(() => {
    if (teamId) {
      loadPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // Add single generator
  const addSingleGenerator = async () => {
    if (!canAdd) {
      return toast.error("You don't have permission to add generators");
    }

    if (!teamId) {
      return toast.error("No team selected");
    }

    const validation = validateGenerator(addData);
    if (!validation.valid) {
      setAddErrors(validation.errors);
      return;
    }

    setAddErrors([]);
    const generatorData = {
      ...validation.data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      source: "manual",
      createdBy: user?.uid || null,
    };

    try {
      // Use name as document ID (normalized)
      const docId = validation.data.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "-");
      await setDoc(
        doc(db, "teams", teamId, "generators_existing", docId),
        generatorData
      );

      toast.success("✓ Generator added successfully");
      setAddData({
        name: "",
        address: "",
        city: "",
        state: "",
        country: "",
        type: "",
        notes: "",
      });
      loadPage();
    } catch (err) {
      toast.error(`Failed to add generator: ${err.message}`);
    }
  };

  // Handle add data change
  const handleAddDataChange = (field, value) => {
    setAddData((prev) => ({ ...prev, [field]: value }));
    setAddErrors([]);
  };

  // Start editing generator
  const startEdit = (id, generator) => {
    if (!canEdit) {
      return toast.error("You don't have permission to edit generators");
    }
    setEditingId(id);
    setEditingData({
      name: generator.name || "",
      address: generator.address || "",
      city: generator.city || "",
      state: generator.state || "",
      country: generator.country || "",
      type: generator.type || "",
      notes: generator.notes || "",
    });
    setEditingErrors([]);
  };

  // Save edited generator
  const saveEdit = async () => {
    if (!canEdit) {
      return toast.error("You don't have permission to edit generators");
    }

    const validation = validateGenerator(editingData);
    if (!validation.valid) {
      setEditingErrors(validation.errors);
      return;
    }

    try {
      const generatorData = {
        ...validation.data,
        updatedAt: serverTimestamp(),
        source: "edit",
        updatedBy: user?.uid || null,
      };

      await setDoc(
        doc(db, "teams", teamId, "generators_existing", editingId),
        generatorData,
        { merge: true }
      );
      toast.success("✓ Generator updated successfully");
      setEditingId(null);
      setEditingData({});
      setEditingErrors([]);
      loadPage();
    } catch (err) {
      toast.error(`Failed to update generator: ${err.message}`);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditingData({});
    setEditingErrors([]);
  };

  // Handle editing data change
  const handleEditingDataChange = (field, value) => {
    setEditingData((prev) => ({ ...prev, [field]: value }));
    setEditingErrors([]);
  };

  // Delete generator
  const confirmDelete = async (id, name) => {
    if (!canDelete) {
      return toast.error("Only administrators can delete generators");
    }

    if (
      !window.confirm(
        `Remove "${name}" from generators?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteDoc(doc(db, "teams", teamId, "generators_existing", id));
      toast.success("✓ Generator removed successfully");
      loadPage();
    } catch (err) {
      toast.error(`Failed to delete generator: ${err.message}`);
    }
  };

  // Handle CSV file selection
  const handleCSVFile = (e) => {
    setCsvFile(e.target.files?.[0] || null);
  };

  // Utility to chunk arrays for batch processing
  const chunk = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  // Import CSV data
  const importCSV = async () => {
    if (!canAdd) {
      return toast.error("You don't have permission to import generators");
    }

    if (!csvFile) {
      return toast.warn("Choose a CSV file");
    }

    if (!teamId) {
      return toast.error("No team selected");
    }

    setImporting(true);
    setImportProgress({
      total: 0,
      processed: 0,
      success: 0,
      skipped: 0,
      errors: 0,
    });

    try {
      const parsed = await new Promise((resolve, reject) => {
        Papa.parse(csvFile, {
          complete: resolve,
          error: reject,
          skipEmptyLines: true,
          header: csvHasHeader,
        });
      });

      let rows = parsed?.data || [];
      if (rows.length === 0) {
        setImporting(false);
        return toast.warn("No rows detected in CSV");
      }

      // Apply row limits based on mode
      const maxRows = testMode ? 100 : 33000;
      if (rows.length > maxRows) {
        rows = rows.slice(0, maxRows);
        toast.info(
          `${
            testMode ? "Test mode: " : ""
          }Limiting to first ${maxRows.toLocaleString()} rows`
        );
      }

      debugLog("CSV rows to process:", rows.length);
      setImportProgress((p) => ({ ...p, total: rows.length }));

      // Process rows and normalize data
      const validGenerators = [];
      let processed = 0;
      let skipped = 0;
      let errors = 0;

      for (const row of rows) {
        processed++;

        try {
          // Handle different CSV formats - adjust field mapping as needed
          const generatorData = {
            name: row.Name || row.name || row.Company || row.company || "",
            address: (
              row.Address1 ||
              row.address1 ||
              row.Address ||
              row.address ||
              ""
            ).trim(),
            city: (row.City || row.city || "").trim(),
            state: (row.State || row.state || "").trim(),
            country: row.Country || row.country || "",
            type: (row.Type || row.type || "").trim(),
            notes: `Imported from CSV. Generator: ${
              row.Generator || row.generator || ""
            }`,
            broker: row.Name || row.name || "", // Broker name from CSV
          };

          // Auto-populate country from state if not provided
          if (!generatorData.country && generatorData.state) {
            generatorData.country = getCountryFromState(generatorData.state);
          }

          // Skip rows with missing essential data
          if (!generatorData.name.trim() && !generatorData.address.trim()) {
            skipped++;
            continue;
          }

          // Clean up empty fields
          if (
            generatorData.name === "#N/A" ||
            generatorData.name === "(blank)"
          ) {
            generatorData.name = "";
          }

          const validation = validateGenerator(generatorData);
          if (validation.valid) {
            validGenerators.push({
              ...validation.data,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              source: "csv_import",
              createdBy: user?.uid || null,
              originalRow: row, // Keep original for debugging
            });
          } else {
            errors++;
            debugLog("Validation failed for row:", row, validation.errors);
          }
        } catch (err) {
          errors++;
          debugLog("Error processing row:", row, err);
        }

        // Update progress periodically
        if (processed % 100 === 0) {
          setImportProgress((p) => ({
            ...p,
            processed,
            skipped,
            errors,
          }));
        }
      }

      debugLog("Valid generators to import:", validGenerators.length);

      // Import in batches to avoid Firestore limits
      const batches = chunk(validGenerators, 400); // Stay under 500 limit
      let success = 0;

      for (const batch of batches) {
        const batchPromises = batch.map((generator) => {
          // Use a normalized name as document ID
          const docId = `${generator.name}-${generator.state || "unknown"}`
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "-")
            .replace(/-+/g, "-")
            .substring(0, 100); // Firestore doc ID limit

          return setDoc(
            doc(db, "teams", teamId, "generators_existing", docId),
            generator,
            { merge: true }
          );
        });

        await Promise.all(batchPromises);
        success += batch.length;

        setImportProgress((p) => ({
          ...p,
          success,
          processed: Math.min(processed, rows.length),
        }));
      }

      toast.success(`✓ Successfully imported ${success} generators`);
      if (skipped > 0) {
        toast.info(`Skipped ${skipped} rows with insufficient data`);
      }
      if (errors > 0) {
        toast.warning(`${errors} rows had validation errors`);
      }

      loadPage(); // Refresh the list
    } catch (err) {
      console.error("CSV import error:", err);
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Tab button component
  const TabButton = ({ id, label, icon: Icon, isActive, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg font-medium transition-colors ${
        isActive
          ? "bg-white text-blue-600 border-b-2 border-blue-600"
          : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-gray-900">
            Generator Compliance Checker
          </h1>
          {selectedTeam && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Team:</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {selectedTeam.name}
              </span>
            </div>
          )}
          {isAdmin && (
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
              Admin
            </span>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {teams && teams.length > 1 && (
            <button
              onClick={onTeamChange}
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded border"
            >
              Change Team
            </button>
          )}

          {onDebugToggle && (
            <div className="flex items-center space-x-2">
              <FaBug
                className={debugEnabled ? "text-green-600" : "text-gray-400"}
              />
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={debugEnabled}
                  onChange={(e) => onDebugToggle(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Debug Logging</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <TabButton
            id="checker"
            label="Check Generator"
            icon={FaSearch}
            isActive={activeTab === "checker"}
            onClick={setActiveTab}
          />
          <TabButton
            id="manage"
            label="Manage Existing"
            icon={FaList}
            isActive={activeTab === "manage"}
            onClick={setActiveTab}
          />
          <TabButton
            id="import"
            label="Bulk Import"
            icon={FaUpload}
            isActive={activeTab === "import"}
            onClick={setActiveTab}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Check Generator Tab */}
        {activeTab === "checker" && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaBuilding className="mr-2 text-blue-600" />
                Check Against Existing Generators
              </h3>
              <p className="text-gray-600 mb-6">
                Verify if a company or address is already in your generator
                database to avoid duplicates and maintain compliance.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={checkName}
                    onChange={(e) => setCheckName(e.target.value)}
                    placeholder="Enter company name to check"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={checkAddress}
                    onChange={(e) => setCheckAddress(e.target.value)}
                    placeholder="Enter address to check"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={checkGenerators}
                disabled={checking || !teamId}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg flex items-center hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaSearch className="mr-2" />
                {checking ? "Checking..." : "Check for Duplicates"}
              </button>
            </div>

            {/* Check Results */}
            {checkResults.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">
                  Similarity Results
                </h3>
                <div className="space-y-3">
                  {checkResults.map((result, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${
                        result.overallMatch
                          ? "bg-red-50 border-red-200"
                          : result.confidence >= 70
                          ? "bg-yellow-50 border-yellow-200"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            {result.overallMatch ? (
                              <FaTimesCircle className="text-red-600" />
                            ) : (
                              <FaExclamationTriangle className="text-yellow-600" />
                            )}
                            <h4 className="font-semibold text-gray-900">
                              {result.generator.name}
                            </h4>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                result.overallMatch
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {result.confidence}% match
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm flex items-center">
                            <FaMapMarkerAlt className="mr-1" />
                            {result.generator.address}
                          </p>
                          <div className="flex space-x-4 mt-2 text-xs text-gray-500">
                            <span>Name: {result.nameMatch}% similar</span>
                            <span>Address: {result.addressMatch}% similar</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Enhanced Matching Guide:
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>
                      • <strong>75%+ name similarity</strong> or{" "}
                      <strong>80%+ address similarity</strong> = Likely
                      duplicate
                    </li>
                    <li>
                      • <strong>50-74% name</strong> or{" "}
                      <strong>60-79% address</strong> similarity = Review
                      recommended
                    </li>
                    <li>
                      • <strong>Partial matching:</strong> "Got Junk" will match
                      "1-800-Got Junk"
                    </li>
                    <li>
                      • <strong>Address normalization:</strong> "123 Main St"
                      matches "123 Main Street"
                    </li>
                    <li>
                      • <strong>Company normalization:</strong> Removes LLC,
                      Inc, Corp, phone prefixes
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manage Generators Tab */}
        {activeTab === "manage" && (
          <div className="p-6">
            {/* Add Generator Section */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaPlus className="mr-2 text-blue-600" />
                Add New Generator
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={addData.name}
                    onChange={(e) =>
                      handleAddDataChange("name", e.target.value)
                    }
                    placeholder="Enter company name"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    value={addData.address}
                    onChange={(e) =>
                      handleAddDataChange("address", e.target.value)
                    }
                    placeholder="Enter address"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={addData.city}
                    onChange={(e) =>
                      handleAddDataChange("city", e.target.value)
                    }
                    placeholder="Enter city"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={addData.state}
                    onChange={(e) => {
                      const state = e.target.value;
                      handleAddDataChange("state", state);
                      // Auto-populate country if state is recognized
                      if (state && !addData.country) {
                        const country = getCountryFromState(state);
                        if (country) {
                          handleAddDataChange("country", country);
                        }
                      }
                    }}
                    placeholder="Enter state/province code"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={addData.country}
                    onChange={(e) =>
                      handleAddDataChange("country", e.target.value)
                    }
                    placeholder="Auto-populated from state"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={addData.type}
                    onChange={(e) =>
                      handleAddDataChange("type", e.target.value)
                    }
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select type</option>
                    <option value="Broker">Broker</option>
                    <option value="Direct">Direct</option>
                    <option value="Internalized">Internalized</option>
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={addData.notes}
                  onChange={(e) => handleAddDataChange("notes", e.target.value)}
                  placeholder="Additional notes or comments"
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {addErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                  <div className="flex items-center text-red-800 mb-2">
                    <FaExclamationTriangle className="mr-2" />
                    <span className="font-medium">
                      Please fix the following errors:
                    </span>
                  </div>
                  <ul className="text-red-700 text-sm space-y-1">
                    {addErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={addSingleGenerator}
                disabled={!canAdd || !teamId}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
              >
                <FaPlus className="mr-2" />
                Add Generator
              </button>
            </div>

            {/* Generators List */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">
                Current Generators ({listGenerators.length} entries)
              </h3>
              <div className="overflow-hidden border rounded-lg">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {manageLoading && (
                      <tr>
                        <td
                          className="px-6 py-8 text-center text-gray-500"
                          colSpan={4}
                        >
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                            Loading generators...
                          </div>
                        </td>
                      </tr>
                    )}
                    {!manageLoading && listGenerators.length === 0 && (
                      <tr>
                        <td
                          className="px-6 py-8 text-center text-gray-500"
                          colSpan={4}
                        >
                          No generators found
                        </td>
                      </tr>
                    )}
                    {listGenerators.map((generator) => (
                      <tr key={generator.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          {editingId === generator.id ? (
                            <input
                              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={editingData.name || ""}
                              onChange={(e) =>
                                handleEditingDataChange("name", e.target.value)
                              }
                            />
                          ) : (
                            <div>
                              <div className="font-medium text-gray-900">
                                {generator.name}
                              </div>
                              {generator.type && (
                                <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded mt-1">
                                  {generator.type}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {editingId === generator.id ? (
                            <input
                              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={editingData.address || ""}
                              onChange={(e) =>
                                handleEditingDataChange(
                                  "address",
                                  e.target.value
                                )
                              }
                            />
                          ) : (
                            <div className="text-sm text-gray-600">
                              {generator.address}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {editingId === generator.id ? (
                            <div className="space-y-2">
                              <input
                                className="w-full p-1 border rounded text-xs"
                                placeholder="City"
                                value={editingData.city || ""}
                                onChange={(e) =>
                                  handleEditingDataChange(
                                    "city",
                                    e.target.value
                                  )
                                }
                              />
                              <input
                                className="w-full p-1 border rounded text-xs"
                                placeholder="State"
                                value={editingData.state || ""}
                                onChange={(e) =>
                                  handleEditingDataChange(
                                    "state",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          ) : (
                            <div className="text-sm">
                              <div>{generator.city}</div>
                              <div className="text-gray-500">
                                {generator.state} {generator.country}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end space-x-2">
                            {editingId === generator.id ? (
                              <>
                                <button
                                  onClick={saveEdit}
                                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 flex items-center text-sm"
                                >
                                  <FaCheck className="mr-1" />
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 flex items-center text-sm"
                                >
                                  <FaTimes className="mr-1" />
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                {canEdit && (
                                  <button
                                    onClick={() =>
                                      startEdit(generator.id, generator)
                                    }
                                    className="bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 flex items-center text-sm"
                                  >
                                    <FaEdit className="mr-1" />
                                    Edit
                                  </button>
                                )}
                                {canDelete ? (
                                  <button
                                    onClick={() =>
                                      confirmDelete(
                                        generator.id,
                                        generator.name
                                      )
                                    }
                                    className="bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 flex items-center text-sm"
                                  >
                                    <FaTrash className="mr-1" />
                                    Delete
                                  </button>
                                ) : (
                                  <span className="text-gray-400 text-xs">
                                    Admin only
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => loadPage({ direction: "prev" })}
                  disabled={!hasPrev || manageLoading}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Previous {PAGE_SIZE}
                </button>
                <span className="text-sm text-gray-600">
                  Showing up to {PAGE_SIZE} entries per page
                </span>
                <button
                  onClick={() => loadPage({ direction: "next" })}
                  disabled={!hasNext || manageLoading}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next {PAGE_SIZE} →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Import Tab */}
        {activeTab === "import" && (
          <div className="p-6">
            <div className="max-w-3xl">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaUpload className="mr-2 text-blue-600" />
                Bulk Import Generators via CSV
              </h3>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Choose CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVFile}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="csvHeader"
                      checked={csvHasHeader}
                      onChange={(e) => setCsvHasHeader(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="csvHeader"
                      className="text-sm text-gray-700"
                    >
                      First row contains headers
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="testMode"
                      checked={testMode}
                      onChange={(e) => setTestMode(e.target.checked)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="testMode" className="text-sm text-gray-700">
                      <span className="text-orange-600 font-medium">
                        Test mode
                      </span>{" "}
                      - Import only first 100 rows
                    </label>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">
                    CSV Format Requirements:
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>
                      • <strong>Expected columns:</strong> ID, Type, Name,
                      Generator, Company, Address1, Address2, State
                    </li>
                    <li>
                      • <strong>Country auto-detection:</strong> Country will be
                      automatically determined from State code
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        Maximum {testMode ? "100" : "33,000"} rows
                      </strong>{" "}
                      per upload (extra rows ignored)
                    </li>
                    <li>
                      • <strong>Duplicate handling:</strong> Records with same
                      name+state will be merged
                    </li>
                    <li>
                      • <strong>Data validation:</strong> Invalid records are
                      skipped with error reporting
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-medium text-yellow-900 mb-2">
                    State-to-Country Mapping Examples:
                  </h4>
                  <div className="text-sm text-yellow-800 grid grid-cols-2 gap-2">
                    <div>• NY, CA, TX → United States</div>
                    <div>• ON, QC, BC → Canada</div>
                    <div>• CH → Switzerland</div>
                    <div>• PR, VI → United States</div>
                  </div>
                </div>

                <button
                  onClick={importCSV}
                  disabled={!csvFile || importing || !canAdd}
                  className={`px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors text-white ${
                    testMode
                      ? "bg-orange-600 hover:bg-orange-700"
                      : "bg-blue-600"
                  }`}
                >
                  <FaUpload className="mr-2" />
                  {importing
                    ? "Importing..."
                    : testMode
                    ? "Import 100 Rows (Test)"
                    : "Import Full CSV"}
                </button>

                {!canAdd && (
                  <p className="text-gray-500 text-sm">
                    You need to be logged in to import generators.
                  </p>
                )}

                {importing && (
                  <div
                    className={`p-4 rounded-lg ${
                      testMode
                        ? "bg-orange-50 border border-orange-200"
                        : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <div
                        className={`animate-spin rounded-full h-4 w-4 border-b-2 mr-2 ${
                          testMode ? "border-orange-600" : "border-blue-600"
                        }`}
                      ></div>
                      <span className="text-sm font-medium">
                        {testMode ? "🧪 Test Import: " : ""}Importing
                        generators...
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        Progress: {importProgress.processed}/
                        {importProgress.total} processed
                      </div>
                      <div>
                        ✓ {importProgress.success} successfully imported
                      </div>
                      <div>
                        ⊘ {importProgress.skipped} skipped (insufficient data)
                      </div>
                      {importProgress.errors > 0 && (
                        <div>⚠️ {importProgress.errors} validation errors</div>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          testMode ? "bg-orange-600" : "bg-blue-600"
                        }`}
                        style={{
                          width: `${
                            importProgress.total > 0
                              ? (importProgress.processed /
                                  importProgress.total) *
                                100
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                    {testMode && (
                      <div className="mt-2 text-xs text-orange-700 bg-orange-100 p-2 rounded">
                        🧪 Test mode active - Only importing first 100 rows for
                        validation
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratorChecker;
